/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import type {ChannelRecord} from '@app/records/ChannelRecord';
import DeveloperOptionsStore from '@app/stores/DeveloperOptionsStore';
import PermissionStore from '@app/stores/PermissionStore';
import SlowmodeStore from '@app/stores/SlowmodeStore';
import UserStore from '@app/stores/UserStore';
import {Permissions} from '@fluxer/constants/src/ChannelConstants';
import {useEffect, useState} from 'react';

type TickListener = () => void;
const tickListeners = new Set<TickListener>();
let tickInterval: ReturnType<typeof setInterval> | null = null;

const fireTick = (): void => {
	for (const listener of tickListeners) listener();
};

const startTicker = (): void => {
	if (tickInterval !== null) return;
	tickInterval = setInterval(fireTick, 1000);
};

const stopTicker = (): void => {
	if (tickInterval === null) return;
	clearInterval(tickInterval);
	tickInterval = null;
};

if (typeof document !== 'undefined') {
	const handleWake = (): void => {
		if (document.hidden) return;
		fireTick();
	};
	document.addEventListener('visibilitychange', handleWake);
	window.addEventListener('pageshow', handleWake);
	window.addEventListener('focus', handleWake);
}

function subscribeToTicker(listener: TickListener): () => void {
	tickListeners.add(listener);
	startTicker();
	return () => {
		tickListeners.delete(listener);
		if (tickListeners.size === 0) stopTicker();
	};
}

interface SlowmodeState {
	isSlowmodeActive: boolean;
	slowmodeRemaining: number;
	canBypass: boolean;
	isSlowmodeEnabled: boolean;
	isSlowmodeImmune: boolean;
}

export function useSlowmode(channel: ChannelRecord): SlowmodeState {
	const [, setTick] = useState(0);
	const currentUser = UserStore.getCurrentUser();
	const lastSendTimestamp = SlowmodeStore.getLastSendTimestamp(channel.id);
	const mockSlowmodeActive = DeveloperOptionsStore.mockSlowmodeActive;
	const mockSlowmodeRemaining = DeveloperOptionsStore.mockSlowmodeRemaining;

	const canBypass = channel.guildId ? PermissionStore.can(Permissions.BYPASS_SLOWMODE, channel) : true;

	const rateLimitPerUser = channel.rateLimitPerUser || 0;

	let slowmodeRemaining = 0;
	if (mockSlowmodeActive) {
		slowmodeRemaining = mockSlowmodeRemaining;
	} else if (currentUser && channel.guildId && rateLimitPerUser && !canBypass && lastSendTimestamp) {
		const timeSinceLastMessage = Date.now() - lastSendTimestamp;
		slowmodeRemaining = Math.max(0, rateLimitPerUser * 1000 - timeSinceLastMessage);
	}

	const isCountingDown = !mockSlowmodeActive && slowmodeRemaining > 0;
	useEffect(() => {
		if (!isCountingDown) return;
		return subscribeToTicker(() => setTick((t) => t + 1));
	}, [isCountingDown]);

	const isSlowmodeEnabled = mockSlowmodeActive || (Boolean(channel.guildId) && rateLimitPerUser > 0);
	const isSlowmodeImmune = !mockSlowmodeActive && isSlowmodeEnabled && canBypass;
	const isSlowmodeActive = mockSlowmodeActive || (!canBypass && rateLimitPerUser > 0 && slowmodeRemaining > 0);

	return {
		isSlowmodeActive,
		slowmodeRemaining,
		canBypass,
		isSlowmodeEnabled,
		isSlowmodeImmune,
	};
}
